import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { addErrorToReport, loadLanguagePack } from '@americanexpress/one-app-ducks';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { sanitize } from '@americanexpress/consumer-travel-base';
import { IntlProvider } from 'react-intl';
import { useSelector } from 'react-redux';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { Surface } from '@americanexpress/dls-react';
import LocationsSearch from './LocationsSearch/LocationsSearchWrapper';
import locationContext from '../context/locationsContext';
import { sanitizeInput } from '../utils/sanitizeInputUtil';
import { moduleName } from '../constants';
import { SEARCH_TYPES } from '../constants/categoryConstants';
import { VALID_LOCATION_KEYS } from '../constants/locationsConstants';
import { TWO_CHARS_MINIMUM_QUERY_LENGTH, hasTwoCharactersSearchRule } from '../constants/featureRulesConstants';
import {
  DOWN_ARROW, END, ENTER, HOME, SHIFT, TAB, UP_ARROW,
} from '../constants/keyConstants';
import { invokeSoftDelete } from '../utils/clearSearchFetchRequest';
import { getClearHistoryAPIUrl } from '../selectors/apiSelectors';
import { useRecentSearchAPI } from '../hooks/apiHooks';
import { CLEAR_SEARCH_POSITION } from '../constants/recentSearchConstants';

const TravelSearchLocations = ({
  id: parentId,
  onChange,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  value,
  type: lineOfBusiness,
  hasWarning,
  minimumQueryLength: upstreamMinimumQueryLength,
  ariaLabel,
  placeholder,
  labelText,
  className,
  locale,
  isRoundTrip,
  languageData,
  direction,
  maximumResultsToShow,
  theme,
  tripType,
  showDemoWrapper,
  errors,
}) => {
  /* istanbul ignore next -- this is a developer warning */
  if (placeholder !== 'Placeholder label') {
    /* Using 'placeholder' as the naming for the label text is confusing and goes against
      * accessibility guidelines which say not to use placeholder for labeling
    */
    console.warn("Please use 'labelText' for the label text");
  }

  const [internalValue, updateInternalValue] = useState({ query: '' });
  const [locationsShown, updateShownLocations] = useState({});
  const [focusedLocationRef, updateFocusedLocationRef] = useState(-1);
  const [shouldDisplayResults, setShouldDisplayResults] = useState(false);
  const [isFullscreenInput, setIsFullscreenInput] = useState(false);
  const [hasRecentSearchDisplayed, updateHasRecentSearchDisplayed] = useState(false);

  const inputWrapperRef = React.useRef(null);
  const fullscreenInputRefocusRef = React.useRef(null);

  const clearHistoryAPIUrl = useSelector(getClearHistoryAPIUrl);
  const { reRunRecentSearchAPI } = useRecentSearchAPI();
  const minimumQueryLength = useConditionalFeature(hasTwoCharactersSearchRule)
    ? TWO_CHARS_MINIMUM_QUERY_LENGTH : upstreamMinimumQueryLength;
  const moduleId = parentId ? `${moduleName}-${parentId}` : moduleName;

  const location = useMemo(() => {
    if (value) {
      return sanitizeInput(value, VALID_LOCATION_KEYS);
    }
    return internalValue;
  }, [value, internalValue]);

  const setLocation = useMemo(() => {
    if (onChange && onChange instanceof Function) {
      return onChange;
    }
    return updateInternalValue;
  }, [onChange]);

  const setQuery = React.useCallback((query) => {
    setLocation({
      query,
    });
    updateFocusedLocationRef(-1);
    setShouldDisplayResults(true);
  }, [setLocation]);

  const onClick = React.useCallback(() => {
    setIsFullscreenInput(true);
    setShouldDisplayResults(true);
  }, []);

  const onFocus = React.useCallback((e) => {
    setShouldDisplayResults(true);
    if (onFocusProp && typeof onFocusProp === 'function') {
      onFocusProp(e);
    }
  }, [onFocusProp]);

  const onCloseFullScreenInput = React.useCallback((event) => {
    // Allow time for onFocus / onBlur events to process before closing
    setTimeout(() => {
      updateFocusedLocationRef(-1);
      event?.stopPropagation();
      setIsFullscreenInput(false);
      setTimeout(() => {
        fullscreenInputRefocusRef.current?.focus();
        fullscreenInputRefocusRef.current?.setSelectionRange(
          fullscreenInputRefocusRef.current.value.length,
          fullscreenInputRefocusRef.current.value.length
        );
        setShouldDisplayResults(false);
      }, 0);
    }, 0);
  }, [isFullscreenInput, fullscreenInputRefocusRef.current]);

  const onClear = React.useCallback(() => {
    updateFocusedLocationRef(-1);
    setQuery('');
  }, [setQuery]);

  const selectLocation = React.useCallback(({
    id,
    airportCode,
    label,
    name,
    type: locationType,
    geoLocation,
    toLabel,
    toAirportCode,
    dates,
    searchIdType,
  }) => {
    const newLocation = {
      id,
      airportCode,
      geoLocation,
      label,
      name: sanitize(name, { html: false }),
      toLabel,
      toAirportCode,
      dates,
      type: locationType,
      query: location.query,
      searchIdType,
    };
    onCloseFullScreenInput();
    setLocation(newLocation);
  }, [location.query, setLocation, onCloseFullScreenInput]);

  // eslint-disable-next-line complexity -- keyboard navigation handler
  const onKeyboardNavigate = React.useCallback((event) => {
    const locationsLastIndex = hasRecentSearchDisplayed
      ? locationsShown.length : locationsShown.length - 1;
    if (event.keyCode !== TAB && event.keyCode !== SHIFT) {
      // Open fullscreen input on any key press except tab or shift.
      // Waits for other event handlers to finish before updating.
      if (!isFullscreenInput) {
        setTimeout(() => {
          setIsFullscreenInput(true);
        }, 0);
      }
    }
    if (event.keyCode === DOWN_ARROW && shouldDisplayResults) {
      event.preventDefault();
      if (focusedLocationRef < locationsLastIndex) {
        updateFocusedLocationRef(focusedLocationRef + 1);
      }
      if (focusedLocationRef === locationsLastIndex) {
        updateFocusedLocationRef(0);
      }
    }
    if (event.keyCode === UP_ARROW && focusedLocationRef > -1 && shouldDisplayResults) {
      event.preventDefault();
      updateFocusedLocationRef(focusedLocationRef - 1);
    }
    if (event.keyCode === HOME && shouldDisplayResults) {
      event.preventDefault();
      updateFocusedLocationRef(0);
    }
    if (event.keyCode === END && shouldDisplayResults) {
      event.preventDefault();
      updateFocusedLocationRef(locationsShown.length - 1);
    }
    if (event.keyCode === ENTER && focusedLocationRef >= 0 && shouldDisplayResults) {
      event.preventDefault();
      if (hasRecentSearchDisplayed && focusedLocationRef === CLEAR_SEARCH_POSITION) {
        invokeSoftDelete({
          clearHistoryAPIUrl, isRoundTrip, lineOfBusiness, reRunRecentSearchAPI,
        });
        onCloseFullScreenInput();
      } else if (hasRecentSearchDisplayed) {
        const nextLocation = locationsShown[focusedLocationRef - 1];
        selectLocation({ ...nextLocation });
        onCloseFullScreenInput();
      } else {
        const nextLocation = locationsShown[focusedLocationRef];
        selectLocation({ ...nextLocation });
        onCloseFullScreenInput();
      }
    }
  }, [
    hasRecentSearchDisplayed,
    locationsShown,
    shouldDisplayResults,
    focusedLocationRef,
    clearHistoryAPIUrl,
    isRoundTrip,
    lineOfBusiness,
    reRunRecentSearchAPI,
    onCloseFullScreenInput,
    selectLocation,
    isFullscreenInput,
  ]);

  const locationProviderValue = useMemo(() => ({
    state: {
      location,
      locationsShown,
      focusedLocationRef,
      shouldDisplayResults,
      isFullscreenInput,
      hasWarning,
      isRoundTrip,
      tripType,
      hasRecentSearchDisplayed,
      errors,
      inputWrapperRef,
      fullscreenInputRefocusRef,
      theme,
    },
    actions: {
      selectLocation,
      updateShownLocations,
      setQuery,
      onCloseFullScreenInput,
      onClick,
      onFocus,
      onClear,
      onBlur: onBlurProp,
      onKeyboardNavigate,
      updateHasRecentSearchDisplayed,
      setShouldDisplayResults,
    },
    config: {
      moduleId,
      lineOfBusiness,
      minimumQueryLength,
      ariaLabel,
      labelText: labelText || placeholder,
      direction,
      maximumResultsToShow,
    },
  }), [
    location,
    locationsShown,
    focusedLocationRef,
    isFullscreenInput,
    shouldDisplayResults,
    hasWarning,
    selectLocation,
    setQuery,
    onCloseFullScreenInput,
    onClick,
    onFocus,
    onBlurProp,
    onClear,
    onKeyboardNavigate,
    moduleId,
    lineOfBusiness,
    minimumQueryLength,
    ariaLabel,
    placeholder,
    labelText,
    direction,
    maximumResultsToShow,
    isRoundTrip,
    hasRecentSearchDisplayed,
    tripType,
    theme,
  ]);

  const isBlueTheme = theme === 'blue';
  const content = (
    <IntlProvider locale={locale} messages={languageData}>
      <locationContext.Provider value={locationProviderValue}>
        <Surface
          data-testid="theme-wrapper"
          variant={isBlueTheme ? 'fg-brand' : 'foreground'}
          className="bg-none"
        >
          <LocationsSearch className={className} />
        </Surface>
      </locationContext.Provider>
    </IntlProvider>
  );

  /* istanbul ignore next -- functionality only for demo route and local testing */
  return showDemoWrapper ? (
    <div className={`position-absolute width-full height-full pad ${isBlueTheme ? 'dls-bright-blue-bg' : 'dls-white-bg'}`}>
      {content}
    </div>
  ) : content;
};

TravelSearchLocations.propTypes = {
  id: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.shape({
    query: PropTypes.string,
    id: PropTypes.string,
    airportCode: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
  }),
  type: PropTypes.string,
  hasWarning: PropTypes.bool,
  minimumQueryLength: PropTypes.number,
  ariaLabel: PropTypes.string,
  placeholder: PropTypes.string,
  labelText: PropTypes.string,
  className: PropTypes.string,
  locale: PropTypes.string.isRequired,
  languageData: PropTypes.shape({}).isRequired,
  direction: PropTypes.string,
  maximumResultsToShow: PropTypes.number,
  isRoundTrip: PropTypes.bool,
  tripType: PropTypes.string,
  theme: PropTypes.string,
  showDemoWrapper: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.string),
};

TravelSearchLocations.defaultProps = {
  type: SEARCH_TYPES.HOTEL.key,
  hasWarning: false,
  minimumQueryLength: 3,
  ariaLabel: undefined,
  placeholder: 'Placeholder label',
  labelText: undefined,
  className: null,
  direction: 'DESTINATION',
  maximumResultsToShow: 10,
  isRoundTrip: false,
  tripType: null,
  showDemoWrapper: false,
};

export const loadModuleData = async ({ store: { dispatch } }) => {
  try {
    await Promise.all([
      dispatch(loadLanguagePack(moduleName, { fallbackLocale: 'en-US' })),
    ]);
  } catch (e) {
    await dispatch(addErrorToReport(e, {
      collectionMethod: 'loadModuleData',
      moduleID: moduleName,
    }));
  }
};

TravelSearchLocations.holocron = {
  name: moduleName,
  loadModuleData,
};

export { TravelSearchLocations as TestableTravelSearchLocations };
export default oneAppModuleWrapper(moduleName)(TravelSearchLocations);
